import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import '@/index.css';
import '@/lib/i18n';
import { SimpleAuthProvider } from '@/components/providers/SimpleAuthProvider';
import { ExchangeRateProvider } from '@/contexts/ExchangeRateContext';
import { LocalStorageProvider } from '@/contexts/LocalStorageContext';
import { ThemeProvider } from '@/components/providers/ThemeProvider';
import { lazy, Suspense } from 'react';
import { DashboardLayout } from './views/DashboardLayout';

// Lazy load all route components for code splitting
const DashboardPage = lazy(() => import('./views/dashboard'));
const PatientsPage = lazy(() => import('./views/patients'));
const PrescriptionsPage = lazy(() => import('./views/prescriptions'));
const CreatePrescriptionPage = lazy(() => import('./views/prescriptions/create'));
const ProfilePage = lazy(() => import('./views/profile'));
const SettingsPage = lazy(() => import('./views/settings'));

// Auth pages can be eagerly loaded as they're needed immediately
import LoginPage from './auth/login';
import SignUpPage from './auth/signup';
import SignUpSuccessPage from './auth/sign-up-success';
import SetupProfilePage from './auth/setup-profile';
import PendingApprovalPage from './auth/pending-approval';
import AcceptInvitePage from './auth/accept-invite';
import ForgotPasswordPage from './auth/forgot-password';
import ResetPasswordPage from './auth/reset-password';
import EmailConfirmationPage from './auth/email-confirmation';
import AuthErrorPage from './auth/error';
import UnauthorizedPage from './auth/unauthorized';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary, NetworkMonitor } from '@/lib/error-handling';
import { Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';

export const ROUTES = {
  AUTH: {
    LOGIN: '/auth/login',
    SIGNUP: '/auth/signup',
    SIGNUP_SUCCESS: '/auth/sign-up-success',
    SETUP_PROFILE: '/auth/setup-profile',
    PENDING_APPROVAL: '/pending-approval',
    ACCEPT_INVITE: '/auth/accept-invite',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    EMAIL_CONFIRMATION: '/auth/email-confirmation',
    ERROR: '/auth/error',
    UNAUTHORIZED: '/auth/unauthorized',
  },
  VIEWS: {
    DASHBOARD: '/dashboard',
    PRESCRIPTIONS: '/prescriptions',
    PRESCRIPTIONS_CREATE: '/prescriptions/create',
    PATIENTS: '/patients',
    // MESSAGES: '/messages', // Removed - not needed for doctor workflow
    PROFILE: '/profile',
    SETTINGS: '/settings',
  }
};

function RouteLoading() {
  const { t } = useTranslation('doctor-platform');

  return <div className="flex items-center justify-center p-8">{t('routeLoading')}</div>;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LocalStorageProvider>
          <SimpleAuthProvider>
            <ExchangeRateProvider>
              <ThemeProvider>
                <NetworkMonitor 
                  showToasts={true}
                  autoRetryFailedRequests={true}
                />
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
                  <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
                  <Route path={ROUTES.AUTH.SIGNUP} element={<SignUpPage />} />
                  <Route path={ROUTES.AUTH.SIGNUP_SUCCESS} element={<SignUpSuccessPage />} />
                  <Route path={ROUTES.AUTH.SETUP_PROFILE} element={<SetupProfilePage />} />
                  <Route path={ROUTES.AUTH.PENDING_APPROVAL} element={<PendingApprovalPage />} />
                  <Route path={ROUTES.AUTH.ACCEPT_INVITE} element={<AcceptInvitePage />} />
                  <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
                  <Route path={ROUTES.AUTH.RESET_PASSWORD} element={<ResetPasswordPage />} />
                  <Route path={ROUTES.AUTH.EMAIL_CONFIRMATION} element={<EmailConfirmationPage />} />
                  <Route path={ROUTES.AUTH.ERROR} element={<AuthErrorPage />} />
                  <Route path={ROUTES.AUTH.UNAUTHORIZED} element={<UnauthorizedPage />} />
                  
                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<DashboardLayout />}>
                      <Route path={ROUTES.VIEWS.DASHBOARD} element={
                        <Suspense fallback={<RouteLoading />}>
                          <DashboardPage />
                        </Suspense>
                      } />
                      <Route path={ROUTES.VIEWS.PRESCRIPTIONS} element={
                        <Suspense fallback={<RouteLoading />}>
                          <PrescriptionsPage />
                        </Suspense>
                      } />
                      <Route path={ROUTES.VIEWS.PRESCRIPTIONS_CREATE} element={
                        <Suspense fallback={<RouteLoading />}>
                          <CreatePrescriptionPage />
                        </Suspense>
                      } />
                      <Route path={ROUTES.VIEWS.PATIENTS} element={
                        <Suspense fallback={<RouteLoading />}>
                          <PatientsPage />
                        </Suspense>
                      } />
                      <Route path={ROUTES.VIEWS.PROFILE} element={
                        <Suspense fallback={<RouteLoading />}>
                          <ProfilePage />
                        </Suspense>
                      } />
                      <Route path={ROUTES.VIEWS.SETTINGS} element={
                        <Suspense fallback={<RouteLoading />}>
                          <SettingsPage />
                        </Suspense>
                      } />
                    </Route>
                  </Route>
                  
                  <Route path="*" element={<Navigate to={ROUTES.AUTH.LOGIN} replace />} />
                </Routes>
                <Toaster 
                  position="top-right"
                  expand={false}
                  richColors
                  closeButton
                />
              </ThemeProvider>
            </ExchangeRateProvider>
          </SimpleAuthProvider>
        </LocalStorageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// Create root once
if (!(window as any)._doctorRootInitialized) {
  const root = createRoot(document.getElementById('app')!);
  root.render(<App />);
  (window as any)._doctorRootInitialized = true;
}

